/**
 * ============================================================================
 * 发表论文内容 - Publications Content
 * ============================================================================
 *
 * 【作用】控制“发表论文”页的标题、副标题、筛选“全部”与搜索框占位符，以及论文列表。
 * 【如何修改】标题/副标题/筛选/占位符改上面四个字段的 en/cn；增删论文在 papers 数组中操作。
 *
 * 【标签颜色映射】标签样式统一在此定义，论文数据中只写 tags 数组，不写 color/bg。
 * 新增标签时在此添加一项即可。
 */
export const TAG_STYLES = {
  Synthesis: { color: "text-green-400", bg: "bg-green-400/10" },
  Measurement: { color: "text-red-400", bg: "bg-red-400/10" },
  Theory: { color: "text-blue-400", bg: "bg-blue-400/10" },
  "Before 2021": { color: "text-pink-400", bg: "bg-pink-400/10" },
};

/** 筛选按钮与标签的展示文案（en/cn）；与 TAG_STYLES 的 key 对应，用于 All 以外的分类 */
export const FILTER_CATEGORY_LABELS = {
  Synthesis: { en: "Synthesis", cn: "合成" },
  Measurement: { en: "Measurement", cn: "表征" },
  Theory: { en: "Theory", cn: "理论" },
};

/** 筛选按钮颜色（与 Synthesis/Measurement/Theory 一致），配合 light/dark 主题 */
export const FILTER_BUTTON_STYLES = {
  Synthesis: {
    selected: { light: "bg-green-600 text-white border border-green-600", dark: "bg-green-500 text-white border border-green-500" },
    unselected: { light: "bg-green-50 text-green-700 border border-green-200 hover:bg-green-100", dark: "bg-green-400/10 text-green-400 border border-green-400/20 hover:bg-green-400/20" },
  },
  Measurement: {
    selected: { light: "bg-red-600 text-white border border-red-600", dark: "bg-red-500 text-white border border-red-500" },
    unselected: { light: "bg-red-50 text-red-700 border border-red-200 hover:bg-red-100", dark: "bg-red-400/10 text-red-400 border border-red-400/20 hover:bg-red-400/20" },
  },
  Theory: {
    selected: { light: "bg-blue-600 text-white border border-blue-600", dark: "bg-blue-500 text-white border border-blue-500" },
    unselected: { light: "bg-blue-50 text-blue-700 border border-blue-200 hover:bg-blue-100", dark: "bg-blue-400/10 text-blue-400 border border-blue-400/20 hover:bg-blue-400/20" },
  },
};

/**
 * 【论文对象参数说明】
 * - id: 唯一标识符（数字即可）
 * - year: 发表年份
 * - title: { en: "...", cn: "..." } 或仅英文字符串
 * - journal: 同上
 * - authors: 字符串，如 "A. B. Author, et al."
 * - tags: 分类数组，如 ["Synthesis", "Measurement"]，可与页面上筛选（Synthesis/Measurement/Theory）匹配；2021 年前的也可含 "Before 2021"
 * - doi: 可选，字符串
 * - pdf: 可选，PDF 放在 public/publication/ 下，路径如 "/publication/paper1.pdf"
 * - toc: 可选，目录图路径；无地址时页面会使用占位图 TOC-waiting.jpg
 */
export const publicationsContent = {
  title: { en: "Publications", cn: "发表论文" },
  subtitle: { en: "Representative works demonstrating our closed-loop approach.", cn: "展示我们闭环研究方法的代表性工作。" },
  filterAll: { en: "All", cn: "全部" },
  /** 分类筛选按钮文案（Synthesis/Measurement/Theory 的 en/cn），与 categories 顺序一致 */
  filterCategories: {
    Synthesis: { en: "Synthesis", cn: "合成" },
    Measurement: { en: "Measurement", cn: "表征" },
    Theory: { en: "Theory", cn: "理论" },
  },
  /** 年份筛选下拉的“全部年份”选项 */
  filterAllYears: { en: "All years", cn: "全部年份" },
  searchPlaceholder: { en: "Search...", cn: "搜索论文..." },
  /** 2021 年以前折叠区标题 */
  Before2021Label: { en: "Before 2021", cn: "2021 年以前" },
  papers: [
    {
      id: 19,
      year: 2026,
      title: {
        en: "Metal-Semiconductor Heterojunction in a Single Graphene Nanoribbon",
        cn: "Metal-Semiconductor Heterojunction in a Single Graphene Nanoribbon"
      },
      journal: {
        en: "submitted",
        cn: "submitted"
      },
      authors: "Ziyi Wang†, Kaitlin Slicker†, Weichen Tang†, Boyu Qie†, Rafal Zuzak, Haowen Pang, Yudi Huang, Xinheng Li, Peter Jacobse, Steven Louie*, Felix Fischer*, Michael Crommie*",
      tags: ["Synthesis","Measurement"],
      doi: "submitted/to be updated",
      pdf: "/publication/paper19.pdf",
      toc: "/images/publications/toc-paper19.jpg",
    },
    {
      id: 18,
      year: 2026,
      title: {
        en: "Engineering phase-frustration-induced flat bands in an aza-triangulene covalent kagome lattice",
        cn: "Engineering phase-frustration-induced flat bands in an aza-triangulene covalent kagome lattice"
      },
      journal: {
        en: "Nature Materials",
        cn: "Nature Materials"
      },
      authors: "Yuyi Yan†, Fujia Liu†, Weichen Tang†, Han Xuan Wong, Boyu Qie*, Steven G. Louie*, Felix R. Fischer*",
      tags: ["Synthesis","Measurement","Theory"],
      doi: "10.1038/s41563-026-02528-3",
      pdf: "/publication/paper18.pdf",
      toc: "/images/publications/toc-paper18.jpg",
    },
    {
      id: 17,
      year: 2026,
      title: {
        en: "Pseudo–Jahn–Teller Distortion in a One-Dimensional π-Conjugated Polymer",
        cn: "Pseudo–Jahn–Teller Distortion in a One-Dimensional π-Conjugated Polymer"
      },
      journal: {
        en: "Journal of the American Chemical Society",
        cn: "Journal of the American Chemical Society"
      },
      authors: "Ziyi Wang†, Boyu Qie†, Weichen Tang†, Jingwei Jiang, Fujia Liu, Peter H Jacobse, Jiaming Lu, Xinheng Li, Steven G Louie*, Felix R Fischer*, Michael F Crommie*",
      tags: ["Synthesis","Measurement"],
      doi: "10.1021/jacs.5c21950",
      pdf: "/publication/paper17.pdf",
      toc: "/images/publications/toc-paper17.jpeg",
    },
    {
      id: 16,
      year: 2025,
      title: {
        en: "One-dimensional heterocyclic carbene–Au metal–organic frameworks bridging ultra-high vacuum models and scalable liquid-phase growth",
        cn: "One-dimensional heterocyclic carbene–Au metal–organic frameworks bridging ultra-high vacuum models and scalable liquid-phase growth"
      },
      journal: {
        en: "Applied Surface Science",
        cn: "Applied Surface Science"
      },
      authors: "Leonardo Cielo, Mattia Cattelan, Luca Schio, Leonardo Longo, Federico Grillo, Boyu Qie, Ziyi Wang, Luca Floreano, Christopher J Baddeley, Francesco Sedona, Cristina Tubaro, Felix R Fischer, Stefano Agnoli",
      tags: ["Synthesis"],
      doi: "10.1016/j.apsusc.2025.165322",
      pdf: "/publication/paper16.pdf",
      toc: "/images/publications/toc-paper16.jpg",
    },
    {
      id: 15,
      year: 2025,
      title: {
        en: "Orbital Engineering Band Degeneracy in a Dual-Square Carbon-Oxide Framework",
        cn: "Orbital Engineering Band Degeneracy in a Dual-Square Carbon-Oxide Framework"
      },
      journal: {
        en: "ACS nano",
        cn: "ACS nano"
      },
      authors: "Fujia Liu†, Yuyi Yan†, Weichen Tang†, Boyu Qie*, Jieqi Chen, Ziyi Wang, Steven G Louie*, Felix R Fischer*",
      tags: ["Synthesis", "Measurement", "Theory"],
      doi: "10.1021/acsnano.5c03671",
      pdf: "/publication/paper15.pdf",
      toc: "/images/publications/toc-paper15.jpeg",
    },
    {
      id: 14,
      year: 2024,
      title: {
        en: "Synthesis and characterization of low-dimensional N-heterocyclic carbene lattices",
        cn: "Synthesis and characterization of low-dimensional N-heterocyclic carbene lattices"
      },
      journal: {
        en: "Science",
        cn: "Science"
      },
      authors: "Boyu Qie†, Ziyi Wang†, Jingwei Jiang†, Zisheng Zhang, Peter H Jacobse, Jiaming Lu, Xinheng Li, Fujia Liu, Anastassia N Alexandrova, Steven G Louie*, Michael F Crommie*, Felix R Fischer*",
      tags: ["Synthesis", "Measurement"],
      doi: "10.1126/science.adm9814",
      pdf: "/publication/paper14.pdf",
      toc: "/images/publications/toc-paper14.png",
    },
    {
      id: 13,
      year: 2023,
      title: {
        en: "Triangle counting rule: An approach to forecast the magnetic properties of benzenoid polycyclic hydrocarbons",
        cn: "Triangle counting rule: An approach to forecast the magnetic properties of benzenoid polycyclic hydrocarbons"
      },
      journal: {
        en: "The Journal of Physical Chemistry Letters",
        cn: "The Journal of Physical Chemistry Letters"
      },
      authors: "Yuyi Yan, Fengru Zheng, Boyu Qie, Jiayi Lu, Hao Jiang, Zhiwen Zhu, Qiang Sun",
      tags: ["Theory"],
      doi: "10.1021/acs.jpclett.3c00570",
      pdf: "/publication/paper13.pdf",
      toc: "/images/publications/toc-paper13.gif",
    },
    {
      id: 12,
      year: 2020,
      title: {
        en: "A novel all-solid-state S-scheme in CdS/ZnTHPP binary nanosystem for hydrogen evolution",
        cn: "A novel all-solid-state S-scheme in CdS/ZnTHPP binary nanosystem for hydrogen evolution"
      },
      journal: {
        en: "International Journal of Hydrogen Energy",
        cn: "International Journal of Hydrogen Energy"
      },
      authors: "Nan Ni, Boyu Qie, Sicen Du, Zhe Sang, Qiushi Wang, Changgong Meng, Yexiang Tong",
      tags: ["Before 2021"],
      doi: "10.1016/j.ijhydene.2022.02.067",
      pdf: "/publication/paper12.pdf",
      toc: "/images/publications/toc-paper12.jpg",
    },
    {
      id: 11,
      year: 2020,
      title: {
        en: "Engineering interfacial adhesion for high-performance lithium metal anode",
        cn: "Engineering interfacial adhesion for high-performance lithium metal anode"
      },
      journal: {
        en: "Nano Energy",
        cn: "Nano Energy"
      },
      authors: "Bingqing Xu, Zhe Liu, Jiangxu Li, Xin Huang, Boyu Qie, Tianyao Gong, Laiyuan Tan, Xiujia Yang, Daniel Paley, Martin Dontigny, Karim Zaghib, Xiangbiao Liao, Qian Cheng, Haowei Zhai, Xi Chen, Long-Qing Chen, Ce-Wen Nan, Yuan-Hua Lin, Yuan Yang",
      tags: ["Before 2021"],
      doi: "10.1016/j.nanoen.2019.104242",
      pdf: "/publication/paper11.pdf",
      toc: "/images/publications/toc-paper11.jpg",
    },
    {
      id: 10,
      year: 2019,
      title: {
        en: "New insights into nail penetration of Li‐ion batteries: effects of heterogeneous contact resistance",
        cn: "New insights into nail penetration of Li‐ion batteries: effects of heterogeneous contact resistance"
      },
      journal: {
        en: "Batteries & Supercaps",
        cn: "Batteries & Supercaps"
      },
      authors: "Meijie Chen, Qin Ye, Changmin Shi, Qian Cheng, Boyu Qie, Xiangbiao Liao, Haowei Zhai, Yurong He, Yuan Yang",
      tags: ["Before 2021"],
      doi: "10.1002/batt.2019000812",
      pdf: "/publication/paper10.pdf",
      toc: "/images/publications/toc-paper10.jpg",
    },
    {
      id: 9,
      year: 2019,
      title: {
        en: "Stabilizing polyether electrolyte with a 4 V metal oxide cathode by nanoscale interfacial coating",
        cn: "Stabilizing polyether electrolyte with a 4 V metal oxide cathode by nanoscale interfacial coating"
      },
      journal: {
        en: "ACS applied materials & interfaces",
        cn: "ACS applied materials & interfaces"
      },
      authors: "Haowei Zhai, Tianyao Gong, Bingqing Xu, Qian Cheng, Daniel Paley, Boyu Qie, Tianwei Jin, Zhenxuan Fu, Laiyuan Tan, Yuan-Hua Lin, Ce-Wen Nan, Yuan Yang",
      tags: ["Before 2021"],
      doi: "10.1021/acsami.9b04932",
      pdf: "/publication/paper9.pdf",
      toc: "/images/publications/toc-paper9.gif",
    },
    {
      id: 8,
      year: 2019,
      title: {
        en: "Rechargeable solid-state lithium metal batteries with vertically aligned ceramic nanoparticle/polymer composite electrolyte",
        cn: "Rechargeable solid-state lithium metal batteries with vertically aligned ceramic nanoparticle/polymer composite electrolyte"
      },
      journal: {
        en: "Nano Energy",
        cn: "Nano Energy"
      },
      authors: "Xue Wang, Haowei Zhai, Boyu Qie, Qian Cheng, Aijun Li, James Borovilas, Bingqing Xu, Changmin Shi, Tianwei Jin, Xiangbiao Liao, Yibin Li, Xiaodong He, Shanyi Du, Yanke Fu, Martin Dontigny, Karim Zaghib, Yuan Yang",
      tags: ["Before 2021"],
      doi: "10.1016/j.nanoen.2019.03.051",
      pdf: "/publication/paper8.pdf",
      toc: "/images/publications/toc-paper8.jpg",
    },
    {
      id: 7,
      year: 2019,
      title: {
        en: "Nonflammable, low-cost, and fluorine-free solvent for liquid electrolyte of rechargeable lithium metal batteries",
        cn: "Nonflammable, low-cost, and fluorine-free solvent for liquid electrolyte of rechargeable lithium metal batteries"
      },
      journal: {
        en: "ACS Applied Materials & Interfaces",
        cn: "ACS Applied Materials & Interfaces"
      },
      authors: "Tianwei Jin, Yini Wang, Zeyu Hui, Boyu Qie, Aijun Li, Daniel Paley, Bingqing Xu, Xue Wang, Adrian Chitu, Haowei Zhai, Tianyao Gong, Yuan Yang",
      tags: ["Before 2021"],
      doi: "10.1021/acsami.8b22156",
      pdf: "/publication/paper7.pdf",
      toc: "/images/publications/toc-paper7.gif",
    },
    {
      id: 6,
      year: 2019,
      title: {
        en: "Full Dissolution of the Whole Lithium Sulfide Family (Li2S8 to Li2S) in a Safe Eutectic Solvent for Rechargeable Lithium–Sulfur Batteries",
        cn: "Full Dissolution of the Whole Lithium Sulfide Family (Li2S8 to Li2S) in a Safe Eutectic Solvent for Rechargeable Lithium–Sulfur Batteries"
      },
      journal: {
        en: "Angewandte Chemie International Edition",
        cn: "Angewandte Chemie International Edition"
      },
      authors: "Qian Cheng, Weiheng Xu, Shiyi Qin, Subhabrata Das, Tianwei Jin, Aijun Li, Alex Ceng Li, Boyu Qie, Pengcheng Yao, Haowei Zhai, Changmin Shi, Xin Yong, Yuan Yang",
      tags: ["Before 2021"],
      doi: "10.1002/anie.201812611",
      pdf: "/publication/paper6.pdf",
      toc: "/images/publications/toc-paper6.jpg",
    },
    {
      id: 5,
      year: 2019,
      title: {
        en: "Microporous battery electrodes from molecular cluster precursors",
        cn: "Microporous battery electrodes from molecular cluster precursors"
      },
      journal: {
        en: "ACS applied materials & interfaces",
        cn: "ACS applied materials & interfaces"
      },
      authors: "Alexander P Aydt, Boyu Qie, Andrew Pinkard, Long Yang, Qian Cheng, Simon JL Billinge, Yuan Yang, Xavier Roy",
      tags: ["Before 2021"],
      doi: "10.1021/acsami.8b18149",
      pdf: "/publication/paper5.pdf",
      toc: "/images/publications/toc-paper5.gif",
    },
    {
      id: 4,
      year: 2019,
      title: {
        en: "Porous insulating matrix for lithium metal anode with long cycling stability and high power",
        cn: "Porous insulating matrix for lithium metal anode with long cycling stability and high power"
      },
      journal: {
        en: "Energy Storage Materials",
        cn: "Energy Storage Materials"
      },
      authors: "Bingqing Xu, Haowei Zhai, Xiangbiao Liao, Boyu Qie, Jyotirmoy Mandal, Tianyao Gong, Laiyuan Tan, Xiujia Yang, Kerui Sun, Qian Cheng, Meijie Chen, Yupeng Miao, Mian Wei, Bin Zhu, Yanke Fu, Aijun Li, Xi Chen, Wei Min, Ce-Wen Nan, Yuan-Hua Lin, Yuan Yang",
      tags: ["Before 2021"],
      doi: "10.1016/j.ensm.2018.11.035",
      toc: "/images/publications/toc-paper4.jpg",
      pdf: "/publication/paper4.pdf",
    },
    {
      id: 3,
      year: 2019,
      title: {
        en: "Accordion-like stretchable Li-ion batteries with high energy density",
        cn: "Accordion-like stretchable Li-ion batteries with high energy density"
      },
      journal: {
        en: "Energy Storage Materials",
        cn: "Energy Storage Materials"
      },
      authors: "Changmin Shi, Tianyang Wang, Xiangbiao Liao, Boyu Qie, Pengfei Yang, Meijie Chen, Xue Wang, Arvind Srinivasan, Qian Cheng, Qin Ye, Alex Ceng Li, Xi Chen, Yuan Yang",
      tags: ["Before 2021"],
      doi: "10.1016/j.ensm.2018.11.019",
      toc: "/images/publications/toc-paper3.jpg",
      pdf: "/publication/paper3.pdf",
    },
    {
      id: 2,
      year: 2018,
      title: {
        en: "High‐energy‐density foldable battery enabled by zigzag‐like design",
        cn: "High‐energy‐density foldable battery enabled by zigzag‐like design"
      },
      journal: {
        en: "Advanced Energy Materials",
        cn: "Advanced Energy Materials"
      },
      authors: "Xiangbiao Liao†, Changmin Shi†, Tianyang Wang†, Boyu Qie†, Youlong Chen, Pengfei Yang, Qian Cheng, Haowei Zhai, Meijie Chen, Xue Wang, Xi Chen*, Yuan Yang*",
      tags: ["Before 2021"],
      doi: "10.1002/aenm.201802998",
      toc: "/images/publications/toc-paper2.jpg",
      pdf: "/publication/paper2.pdf",
    },
    {
      id: 1,
      year: 2018,
      title: {
        en: "Bioinspired, spine-like, flexible, rechargeable lithium-ion batteries with high energy density",
        cn: "Bioinspired, spine-like, flexible, rechargeable lithium-ion batteries with high energy density"
      },
      journal: {
        en: "Advanced Materials",
        cn: "Advanced Materials"
      },
      authors: "Guoyu Qian, Bin Zhu, Xiangbiao Liao, Haowei Zhai, Arvind Srinivasan, Nathan Joseph Fritz, Qian Cheng, Mingqiang Ning, Boyu Qie, Yi Li, Songliu Yuan, Jia Zhu, Xi Chen, Yuan Yang*",
      tags: ["Before 2021"],
      doi: "10.1002/adma.201704947",
      toc: "/images/publications/toc-paper1.jpg",
      pdf: "/publication/paper1.pdf",
    },
  ]
};
